import { useAuthStore } from '../stores/authStore';
import {
  enqueueSyncEntity,
  getSyncMetadata,
  getSyncOutbox,
  SyncEntityKey,
} from './localStorageService';
import { manifestSyncService } from './manifestSyncService';

const LOCAL_CHANGE_DEBOUNCE_MS = 3000;
const PERIODIC_SYNC_MS = 60000;

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let syncRunning = false;
let rerunRequested = false;

export function queueSyncEntity(entityKey: SyncEntityKey): void {
  const { user } = useAuthStore.getState();
  if (user) {
    const metadataKey: SyncEntityKey = entityKey.startsWith('portrait:')
      ? `warband:${entityKey.split(':')[1]}`
      : entityKey;
    const baseRevision = getSyncMetadata(user.sub)[metadataKey]?.revision ?? null;
    enqueueSyncEntity(user.sub, entityKey, baseRevision);
  } else {
    enqueueSyncEntity('__unassigned__', entityKey, null);
  }
  requestSync();
}

export function requestSync(immediate = false): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(
    () => void flushSyncOutbox(),
    immediate ? 0 : LOCAL_CHANGE_DEBOUNCE_MS
  );
}

export async function flushSyncOutbox(): Promise<void> {
  if (syncRunning) {
    rerunRequested = true;
    return;
  }
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;

  const { user, accessToken, connectionStatus, setSyncStatus } = useAuthStore.getState();
  if (!user || !accessToken || connectionStatus !== 'connected') return;

  syncRunning = true;
  setSyncStatus('syncing');
  try {
    const result = await manifestSyncService.reconcileManifest(accessToken, user.sub);
    useAuthStore.getState().setPendingSyncConflicts(result.conflicts);
    useAuthStore.getState().setPendingImageConflicts(result.imageConflicts);

    if (result.changedLocally) {
      const { useWarbandStore } = await import('../stores/warbandStore');
      const { getAllWarbands } = await import('./localStorageService');
      const state = useWarbandStore.getState();
      state.setWarbands(getAllWarbands());
      if (state.selectedWarbandId) await state.fetchPlayWarbandData(state.selectedWarbandId);

      const { useReferenceDataStore } = await import('../stores/referenceDataStore');
      const referenceState = useReferenceDataStore.getState();
      referenceState.reloadCustomCreatures();
      referenceState.reloadCustomWeapons();
      referenceState.reloadCustomArmours();
      referenceState.reloadCustomBuffs();
    }

    setSyncStatus(
      result.conflicts.length > 0 || result.imageConflicts.length > 0
        ? 'idle'
        : getSyncOutbox(user.sub).length > 0 ? 'idle' : 'success'
    );
  } catch (error) {
    setSyncStatus('error', error instanceof Error ? error.message : 'Sync failed');
  } finally {
    syncRunning = false;
    if (rerunRequested) {
      rerunRequested = false;
      requestSync(true);
    }
  }
}

export function startSyncCoordinator(): () => void {
  const trigger = () => requestSync(true);
  const triggerWhenVisible = () => {
    if (document.visibilityState === 'visible') trigger();
  };

  requestSync(true);
  const interval = setInterval(trigger, PERIODIC_SYNC_MS);
  window.addEventListener('online', trigger);
  window.addEventListener('focus', trigger);
  document.addEventListener('visibilitychange', triggerWhenVisible);

  return () => {
    clearInterval(interval);
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    window.removeEventListener('online', trigger);
    window.removeEventListener('focus', trigger);
    document.removeEventListener('visibilitychange', triggerWhenVisible);
  };
}